import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import chevronLeft from "@assets/icons/chevron-left.svg";
import chevronRight from "@assets/icons/chevron-right.svg";
import questionTickIcon from "@assets/icons/question-tick.svg";
import styles from "./QuestionsCarousel.module.css";

interface QuestionNumberIndicatorProps {
  currentNumber: number;
  isAnswered: boolean;
  isSelected: boolean;
  selectQuestion: () => void;
}

function QuestionNumberIndicator({
  currentNumber,
  isAnswered,
  isSelected,
  selectQuestion,
}: QuestionNumberIndicatorProps) {
  return (
    <div
      className={`${styles.questionIndicator} ${
        isAnswered && isSelected
          ? styles.answeredAndSelected
          : isAnswered
          ? styles.answered
          : isSelected
          ? styles.selected
          : ""
      }`}
      onClick={selectQuestion}
    >
      {isAnswered && <img src={questionTickIcon} alt="Answered" />}
      <span className={styles.questionNumber}>{currentNumber}</span>
    </div>
  );
}

interface QuestionsCarouselProps {
  questions: any[];
  questionIndex: number;
  onQuestionSelect: (index: number) => void;
}

function QuestionsCarousel({
  questions,
  questionIndex,
  onQuestionSelect,
}: QuestionsCarouselProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    containScroll: "trimSnaps",
    dragFree: true,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollButtons = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    updateScrollButtons();
    emblaApi.on("select", updateScrollButtons);
    emblaApi.on("reInit", updateScrollButtons);

    return () => {
      emblaApi.off("select", updateScrollButtons);
      emblaApi.off("reInit", updateScrollButtons);
    };
  }, [emblaApi, updateScrollButtons]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
    }
  }, [emblaApi]);

  const scrollTo = useCallback(
    (index: number) => {
      if (emblaApi) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi]
  );

  // Sync external questionIndex changes with carousel
  useEffect(() => {
    if (emblaApi && questionIndex !== undefined) {
      emblaApi.scrollTo(questionIndex);
    }
  }, [emblaApi, questionIndex]);

  return (
    <div className={styles.questionsNavigationWrapper}>
      {canScrollPrev && (
        <div className={styles.chevronIcons} onClick={scrollPrev}>
          <img src={chevronLeft} alt="Previous" />
        </div>
      )}

      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {questions.map((question, index) => (
            <div key={index} className={styles.emblaSlide}>
              <QuestionNumberIndicator
                currentNumber={index + 1}
                isAnswered={!!question.current_evaluation}
                isSelected={index === questionIndex}
                selectQuestion={() => {
                  onQuestionSelect(index);
                  scrollTo(index);
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {canScrollNext && (
        <div className={styles.chevronIcons} onClick={scrollNext}>
          <img src={chevronRight} alt="Next" />
        </div>
      )}
    </div>
  );
}

export default QuestionsCarousel;
